import { authDevicePayload } from '../api/billing'
import type { LoginResponseRaw, TokenRefreshResponseRaw } from '@provisium/web-api'

import { isOperatorUser, mapApiRedirectPath } from '../auth/routing'
import { apiRequest, setAccessToken } from './client'
import type { AuthSession, MeResponse } from './types'

type LoginRequest = {
  email: string
  password: string
  device_id: string
  device_label?: string
  force?: boolean
}

export type LoginResult = {
  session: AuthSession
  redirectPath: string
}

const normalizeLoginEmail = (email: string): string => {
  const trimmed = email.trim().toLowerCase()
  return trimmed.includes('@') ? trimmed : `${trimmed}@provisium.local`
}

export const refreshAccessToken = async (refreshToken: string) => {
  const device = authDevicePayload()
  const response = await apiRequest<TokenRefreshResponseRaw>('/auth/refresh', {
    method: 'POST',
    body: { refresh_token: refreshToken, ...device },
    auth: false,
  })
  return {
    accessToken: response.access_token,
    refreshToken: response.refresh_token ?? refreshToken,
  }
}

export const login = async (
  email: string,
  password: string,
  force = false,
): Promise<LoginResult> => {
  const normalizedEmail = normalizeLoginEmail(email)
  const device = authDevicePayload()

  const response = await apiRequest<LoginResponseRaw>('/auth/login', {
    method: 'POST',
    body: {
      email: normalizedEmail,
      password,
      ...device,
      ...(force ? { force: true } : {}),
    } satisfies LoginRequest,
    auth: false,
  })

  setAccessToken(response.access_token)

  const me = await apiRequest<MeResponse>('/me')
  const organization = me.organizations[0]
  const operatorAccount = isOperatorUser(me.roles)

  if (!organization && !operatorAccount) {
    throw new Error('У аккаунта нет организации. Обратитесь в поддержку Провизиум.')
  }

  return {
    session: {
      accessToken: response.access_token,
      refreshToken: response.refresh_token,
      organizationId: organization?.id ?? 0,
      email: me.email,
      displayName: me.displayName,
    },
    redirectPath: operatorAccount ? '/operator' : mapApiRedirectPath(response.redirect_path),
  }
}

export const logout = async (session: AuthSession | null) => {
  if (!session?.refreshToken) {
    setAccessToken(null)
    return
  }
  const device = authDevicePayload()
  try {
    await apiRequest<void>('/auth/logout', {
      method: 'POST',
      body: { refresh_token: session.refreshToken, device_id: device.device_id },
    })
  } catch {
    // Client clears local session even if revoke fails.
  }
  setAccessToken(null)
}

type RegisterAccountRequestBody = {
  org_type: 'buyer' | 'supplier'
  email: string
  password: string
  first_name?: string
  last_name?: string
  contact_phone?: string
  legal_name: string
  inn: string
  city: string
  trade_name: string
  consent?: boolean
  consent_offer: boolean
  marketing_consent?: boolean
  notifications_consent?: boolean
  plan?: 'start' | 'month' | 'year'
  utm_source?: string
  utm_medium?: string
  utm_campaign?: string
  horeca_type?: string
  product_type_ids?: number[]
}

type RegisterAccountResponseRaw = {
  access_token: string
  refresh_token: string
  organization: { id: number }
  message?: string | null
  redirect_path?: string | null
  plan?: string | null
  trial_started?: boolean
  trial_ends_at?: string | null
  requires_checkout?: boolean
}

export type RegisterAccountResult = {
  accessToken: string
  refreshToken: string
  organizationId: number
  email: string
  displayName: string
  redirectPath: string
  plan: string
  trialStarted: boolean
  trialEndsAt: string | null
  requiresCheckout: boolean
}

export const registerAccount = async (request: RegisterAccountRequestBody): Promise<RegisterAccountResult> => {
  const email = normalizeLoginEmail(request.email)
  const response = await apiRequest<RegisterAccountResponseRaw>('/auth/register', {
    method: 'POST',
    body: { ...request, email },
    auth: false,
  })

  setAccessToken(response.access_token)
  const me = await apiRequest<MeResponse>('/me')
  const organization = me.organizations.find((item) => item.id === response.organization.id) ?? me.organizations[0]
  if (!organization) {
    throw new Error('Организация не создана')
  }

  return {
    accessToken: response.access_token,
    refreshToken: response.refresh_token,
    organizationId: organization.id,
    email: me.email,
    displayName: me.displayName,
    redirectPath: response.redirect_path?.trim() || '/',
    plan: response.plan ?? request.plan ?? 'start',
    trialStarted: Boolean(response.trial_started),
    trialEndsAt: response.trial_ends_at ?? null,
    requiresCheckout: Boolean(response.requires_checkout),
  }
}

export const requestPasswordReset = async (email: string) => {
  const normalizedEmail = normalizeLoginEmail(email)
  return apiRequest<{ message?: string }>('/auth/forgot-password', {
    method: 'POST',
    body: { email: normalizedEmail },
    auth: false,
  })
}

export const confirmPasswordReset = async (token: string, password: string) => {
  return apiRequest<{ message?: string }>('/auth/reset-password', {
    method: 'POST',
    body: { token, password },
    auth: false,
  })
}
